/**
 * Teslyn Cloud's documents read from a photo or scan (F03, F09; decision 507; docs/specs/document-reading.md §3, §6):
 * the project's pictures and the records they are checked against, adding a picture (the row, then its file in bucket
 * `documents`, then its project_file record), saving the checked values, removing a picture, a 5-minute signed link
 * to a picture, and reading it with the `document-read` function. RLS decides: staff read, the writer roles write.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DOCUMENT_BUCKET,
  DOCUMENT_FILE_TYPES,
  DOCUMENT_READ_IMAGE_MAX_BYTES,
  DOCUMENT_READ_MAX_PX,
  documentFileProblem,
  documentObjectPath,
  importedImageExt,
  importedImageName,
  isBitmapExtension,
  readDocumentPicture,
  readMediaType,
  type Aircraft,
  type DailyEntry,
  type DocumentReadMediaType,
  type DocumentReadOutcome,
  type DocumentScan,
  type FuelLedgerEntry,
} from '@teslyn/domain';
import { all, plainPortalError, type Res } from './api.ts';
import { fileBytes } from '../components/fileBytes.ts';
import { browserPhotoRenderer } from './photoImages.ts';

export interface DocumentsData {
  readonly scans: readonly DocumentScan[];
  /** Pictures whose file is in Teslyn Cloud (a project_file row). */
  readonly uploaded: ReadonlySet<string>;
  readonly aircraft: readonly Aircraft[];
  readonly entries: readonly DailyEntry[];
  readonly fuel: readonly FuelLedgerEntry[];
  readonly mrWarnH: number | null;
  readonly drumL: number | null;
}

const must = <T>(r: Res<T>): T => {
  if (r.error) throw new Error(plainPortalError(r.error));
  return r.data as T;
};

export async function loadDocumentsData(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string },
): Promise<DocumentsData> {
  const [scans, files, aircraft, entries, fuel, settings] = await Promise.all([
    all<DocumentScan>((from, to) =>
      sb
        .from('document_scan')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('created_at')
        .range(from, to),
    ),
    all<{ record_id: string }>((from, to) =>
      sb
        .from('project_file')
        .select('record_id')
        .eq('project_id', project.id)
        .eq('kind', 'document')
        .order('record_id')
        .range(from, to),
    ),
    all<Aircraft>((from, to) =>
      sb
        .from('aircraft')
        .select('*')
        .eq('tenant_id', project.tenantId)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<DailyEntry>((from, to) =>
      sb
        .from('daily_entry')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    all<FuelLedgerEntry>((from, to) =>
      sb
        .from('fuel_ledger_entry')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('id')
        .range(from, to),
    ),
    sb
      .from('project')
      .select('mr_warn_h, fuel_drum_l')
      .eq('id', project.id)
      .maybeSingle() as unknown as Promise<
      Res<{ mr_warn_h: number | null; fuel_drum_l: number | null } | null>
    >,
  ]);
  const s = must(settings);
  return {
    scans,
    uploaded: new Set(files.map((f) => f.record_id)),
    aircraft,
    entries,
    fuel,
    mrWarnH: s?.mr_warn_h ?? null,
    drumL: s?.fuel_drum_l ?? null,
  };
}

/** SHA-256 of the bytes in lower-case hex. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface AddPicture {
  readonly kind: DocumentScan['kind'];
  readonly aircraftId: string | null;
  readonly fuelEntryId: string | null;
  readonly dailyEntryId: string | null;
  readonly file: File;
}

/** Keep a picture chosen in the browser: its row, its file in `documents`, and the file's record. */
export async function addDocumentPicture(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string },
  a: AddPicture,
  newId: () => string = () => globalThis.crypto.randomUUID(),
  toPng: (file: Blob) => Promise<Blob> = browserPhotoRenderer.toPng,
): Promise<{ readonly scan: DocumentScan; readonly bytes: Uint8Array }> {
  const problem = documentFileProblem(a.file.name, a.file.size);
  if (problem) throw new Error(problem);
  const dot = a.file.name.lastIndexOf('.');
  const given = a.file.name.slice(dot + 1).toLowerCase();
  // a bitmap is kept as a PNG, pixel for pixel, under its new name (R144): the browser converts it here
  const ext = importedImageExt(given) as DocumentScan['file_ext'];
  const file: Blob = isBitmapExtension(given) ? await toPng(a.file) : a.file;
  const fileName = importedImageName(a.file.name);
  const bytes = await fileBytes(file);
  const sha256 = await sha256Hex(bytes);
  const id = newId();
  const row = {
    id,
    tenant_id: project.tenantId,
    project_id: project.id,
    kind: a.kind,
    aircraft_id: a.aircraftId,
    fuel_entry_id: a.kind === 'fuel_docket' ? a.fuelEntryId : null,
    daily_entry_id: a.kind === 'fuel_receipt' ? a.dailyEntryId : null,
    file_name: fileName.slice(0, 255),
    file_ext: ext,
    file_path: '',
    size_bytes: bytes.byteLength,
    sha256,
    source: 'cloud',
  };
  const scan = must(
    (await sb.from('document_scan').insert(row).select('*').maybeSingle()) as Res<DocumentScan>,
  );
  const path = documentObjectPath(project.tenantId, project.id, id, ext);
  const up = await sb.storage.from(DOCUMENT_BUCKET).upload(path, bytes, {
    contentType: DOCUMENT_FILE_TYPES[ext] ?? 'application/octet-stream',
    upsert: false,
  });
  if (up.error) throw new Error(`The picture was not stored. ${plainPortalError(up.error)}`);
  must(
    (await sb.from('project_file').insert({
      tenant_id: project.tenantId,
      project_id: project.id,
      kind: 'document',
      record_id: id,
      bucket: DOCUMENT_BUCKET,
      object_path: path,
      file_name: row.file_name,
      content_type: DOCUMENT_FILE_TYPES[ext] ?? 'application/octet-stream',
      size_bytes: bytes.byteLength,
      sha256,
      source: 'portal',
    })) as Res<null>,
  );
  return { scan, bytes };
}

/** Save the checked values (and the reading) of a picture. */
export async function saveDocumentValues(
  sb: SupabaseClient,
  scan: Pick<
    DocumentScan,
    'id' | 'checked_values' | 'read_values' | 'read_by' | 'read_at' | 'checked_by_name' | 'checked_at'
  >,
): Promise<DocumentScan> {
  const { id, ...values } = scan;
  return must(
    (await sb
      .from('document_scan')
      .update(values)
      .eq('id', id)
      .select('*')
      .maybeSingle()) as Res<DocumentScan>,
  );
}

export async function removeDocumentPicture(sb: SupabaseClient, id: string): Promise<void> {
  must(
    (await sb
      .from('document_scan')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)) as Res<null>,
  );
}

/** A 5-minute signed link to a picture in Teslyn Cloud. */
export async function documentPictureUrl(
  sb: SupabaseClient,
  project: { readonly id: string; readonly tenantId: string },
  scan: Pick<DocumentScan, 'id' | 'file_ext'>,
): Promise<string> {
  const { data, error } = await sb.storage
    .from(DOCUMENT_BUCKET)
    .createSignedUrl(documentObjectPath(project.tenantId, project.id, scan.id, scan.file_ext), 300);
  if (error || !data) throw new Error(plainPortalError(error ?? { message: 'No link to the picture.' }));
  return data.signedUrl;
}

/**
 * The picture as the reader takes it: a PDF as it is; a photo fitted to 2,400 px as JPEG when the browser can draw
 * it, else as it is when it is small enough.
 */
export async function pictureForReading(
  bytes: Uint8Array,
  fileExt: string,
  fit: (blob: Blob) => Promise<Uint8Array | null> = fitPhoto,
): Promise<{ readonly bytes: Uint8Array; readonly mediaType: DocumentReadMediaType }> {
  if (fileExt === 'pdf') return { bytes, mediaType: 'application/pdf' };
  const fitted = await fit(
    new Blob([bytes as Uint8Array<ArrayBuffer>], { type: DOCUMENT_FILE_TYPES[fileExt] ?? 'image/jpeg' }),
  );
  if (fitted) return { bytes: fitted, mediaType: 'image/jpeg' };
  const own = DOCUMENT_FILE_TYPES[fileExt] as DocumentReadMediaType | undefined;
  if (own && bytes.byteLength <= DOCUMENT_READ_IMAGE_MAX_BYTES) return { bytes, mediaType: own };
  throw new Error('This browser cannot make the photo small enough to read. Type the values from the photo.');
}

async function fitPhoto(blob: Blob): Promise<Uint8Array | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  try {
    const img = await createImageBitmap(blob);
    const scale = Math.min(1, DOCUMENT_READ_MAX_PX / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // white behind a transparent PNG, as Teslyn Field draws it (a canvas colour, not a page colour)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const out = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', 0.9));
    return out ? await fileBytes(out) : null;
  } catch {
    return null;
  }
}

/** Read a picture with Teslyn Cloud's document-read function, as the signed-in person. */
export async function readDocument(
  sb: SupabaseClient,
  supabaseUrl: string,
  publishableKey: string,
  scan: Pick<DocumentScan, 'project_id' | 'kind' | 'file_ext'>,
  bytes: Uint8Array,
): Promise<DocumentReadOutcome> {
  let picture: { bytes: Uint8Array; mediaType: DocumentReadMediaType };
  try {
    picture = await pictureForReading(bytes, scan.file_ext);
  } catch (e) {
    return { kind: 'refused', message: e instanceof Error ? e.message : String(e) };
  }
  return readDocumentPicture(
    {
      supabaseUrl,
      publishableKey,
      token: async () => (await sb.auth.getSession()).data.session?.access_token ?? null,
      fetch: (u, i) => fetch(u, i),
    },
    {
      projectId: scan.project_id,
      kind: scan.kind,
      mediaType: picture.mediaType ?? readMediaType(scan.file_ext),
      bytes: picture.bytes,
    },
  );
}
